"use client";

import { RetrievedProductCategory, RetrievedService } from "@/lib/sanity/responseTypes";

import Envelope from "@/components/icons/envelope-outline.svg";
import { formatMoney } from "@/lib/money-utils";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import ServiceRequestForm from "./ServiceRequestForm";

interface ServicesGalleryProps {
  locale: string;
  itemsPerRow: 1 | 2 | 3;
  itemWidth: 400;
  imageHeight: 275;
  services: RetrievedService[];
  productCategories: RetrievedProductCategory[];
  gapX?: 20 | 40 | 60;
  gapY?: 20 | 40;
}

export default function ServicesGallery(props: ServicesGalleryProps) {
  const itemWidth = Math.round(props.itemWidth);
  const imageHeight = Math.round(props.imageHeight);

  const { locale, services } = props;
  const t = useTranslations('Services');
  const serviceRequestModalRef = useRef<HTMLDialogElement | null>(null);
  const [selectedService, setSelectedService] = useState<RetrievedService | null>(null);

  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [errorToast, setErrorToast] = useState<string | null>(null);

  useEffect(() => {
    if (successToast && successToast.length > 0) {
      setTimeout(() => {
        setSuccessToast(null);
      }, 5000);
    }
  }, [successToast]);

  useEffect(() => {
    if (errorToast && errorToast.length > 0) {
      setTimeout(() => {
        setErrorToast(null);
      }, 5000);
    }
  }, [errorToast]);

  const itemsPerRowClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-3",
  };

  const aspectRatios = {
    '16/11': "aspect-16/11",
    '10/13': "aspect-10/13",
  };

  const widthClasses = {
    400: 'w-[400px]',
  }

  const heightClasses = {
    275: 'h-[275px]'
  }

  const gapXClasses = {
    20: 'gap-x-5',
    40: 'gap-x-5 md:gap-x-10',
    60: 'gap-x-5 md:gap-x-10 lg:gap-x-[60px]',
  };

  const gapYClasses = {
    20: 'gap-y-5',
    40: 'gap-y-5 md:gap-y-10',
  };

  function setSelectedServiceAndShowModal(s: RetrievedService) {
    setSelectedService(s);
    serviceRequestModalRef.current?.showModal();
  }

  function closeModal() {
    serviceRequestModalRef.current?.close();
    setSelectedService(null);
  }

  return (
    <div className="gap-5">
      <div className={`w-full relative overflow-hidden grid ${itemsPerRowClasses[props.itemsPerRow]} ${props.gapX ? gapXClasses[props.gapX] : gapXClasses[20]} ${props.gapY ? gapYClasses[props.gapY] : gapYClasses[20]} items-start justify-between py-5 box-border text-2xl text-black font-[Outfit]`}>
        {services.map((s, i) => {
          return (
            <div key={`service-${i}`} className={`flex-1 overflow-hidden shrink-0 flex flex-col items-start gap-2.5`}>
              <img src={`${s.imageUrl}?w=${itemWidth * 2}&h=${imageHeight * 2}`} className="aspect-16/11 rounded-3xl w-full max-h-full overflow-hidden object-cover" width={itemWidth} height={imageHeight} />
              <div className="self-stretch leading-[28px] font-semibold text-wrap">{s.name[locale]}</div>
              {s.price && s.price !== 0 && (<div className="self-stretch leading-[120%] text-wrap text-base">{formatMoney(s.price)}</div>) || null}
              <div className="self-stretch leading-[120%] text-wrap text-base">{s.description[locale]}</div>
              <div className="flex flex-col py-2.5">
                <button className="btn btn-secondary text-white rounded-full" onClick={() => setSelectedServiceAndShowModal(s)}>
                  <Envelope className="w-3.5 h-3.5 stroke-white" />
                  {t('cta')}
                </button>
              </div>
            </div>
          )
        })}
      </div>
      <dialog id="service_req_modal" className="modal" ref={serviceRequestModalRef}>
        <div className="modal-box w-11/12 xl:w-8/12 max-w-5xl">
          <h3 className="font-bold text-lg font-[Outfit]">{selectedService?.name[locale]}</h3>
          <form method="dialog">
            {/* if there is a button in form, it will close the modal */}
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
          </form>
          {selectedService && (<ServiceRequestForm
            service={selectedService}
            productCategories={props.productCategories}
            submitPath="/api/service-request"
            close={closeModal}
            setSuccessToast={setSuccessToast}
            setErrorToast={setErrorToast}
          />)}
        </div>
      </dialog>
      <div className="toast toast-end z-1000">
        {successToast && (<div className="alert alert-success">
          <span>{successToast}</span>
        </div>)}
        {errorToast && (<div className="alert alert-error">
          <span>{errorToast}</span>
        </div>)}
      </div>
    </div>
  );
}
